import { api, type Fixture, type Incident } from './api/client.ts';

/**
 * The score ticker: every match of the chosen competitions, so a set of
 * conference channels shows what is happening on the pitches it is not
 * currently watching.
 *
 * Which competitions a set shows is remembered per set. A set nobody has
 * configured yet starts with whatever competition its name mentions -- a set
 * called "Champions League Dienstag" wants exactly that.
 */

const KEY = 'multiview.ticker';
/** How the ticker looks -- a matter of the screen, so per device, not per set. */
const STYLE_KEY = 'multiview.tickerStyle';
/** Earlier home of the crest choice alone. */
const LOGOS_KEY = 'multiview.tickerLogos';

export const TICKER_SIZES = [
  { value: 1, label: 'Normal' },
  { value: 1.3, label: 'Groß' },
  { value: 1.6, label: 'Sehr groß' },
] as const;

/** Scrolling speed in screen widths per second. */
export const TICKER_SPEEDS = [
  { value: 1 / 18, label: 'Langsam' },
  { value: 1 / 12, label: 'Mittel' },
  { value: 1 / 8, label: 'Schnell' },
] as const;

/** Volume of the announcement sounds; 0 is off. */
export const SOUND_VOLUMES = [
  { value: 0, label: 'Aus' },
  { value: 0.3, label: 'Leise' },
  { value: 0.6, label: 'Mittel' },
  { value: 1, label: 'Laut' },
] as const;

export interface TickerStyle {
  /** Crests instead of club names. */
  logos: boolean;
  size: number;
  speed: number;
  /** Cheer, whistle and groan with the announcements. */
  sound: number;
}

const DEFAULT_STYLE: TickerStyle = { logos: false, size: 1, speed: 1 / 12, sound: 0 };
// The proxy refreshes ESPN every 15 s; a slower poll here only added delay.
const POLL_MS = 10_000;
/** How long a changed score stays highlighted. */
const HIGHLIGHT_MS = 2 * 60_000;

interface SetChoice {
  on: boolean;
  competitions: string[];
  goals?: boolean;
  /** Red cards, second yellows and penalties as banners. */
  cards?: boolean;
  /** Yellow cards as a small note in the corner. */
  yellow?: boolean;
}

/** A goal -- or with `incident`, a card or penalty -- to announce on screen. */
export interface GoalBanner {
  id: number;
  fixture: Fixture;
  side: 'home' | 'away' | null;
  incident?: Incident;
}

/** A yellow card, shown small and briefly. */
export interface CardNote {
  id: number;
  fixture: Fixture;
  incident: Incident;
}

/** How long one banner stays; queued goals follow each other. */
export const BANNER_MS = 8000;
const NOTE_MS = 7000;
/**
 * How long a goal banner waits for its scorer. FotMob lists the goal itself a
 * poll or two after the score, and the name sometimes minutes later; the
 * picture runs well behind live anyway, so a short wait costs little.
 */
const SCORER_WAIT_MS = 90_000;
/** Poll interval while a goal waits for its scorer. */
const PENDING_POLL_MS = 4000;

/** Words too general to tell competitions apart. */
const GENERIC = new Set(['league', 'liga', 'leauge', 'football', 'national', 'division', 'cup']);

const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[^a-z0-9 ]/g, ' ');

/** Competitions whose distinctive words appear in the set's name. */
export function guessCompetitions(setName: string, competitions: string[]): string[] {
  const name = norm(setName).replace(/ /g, '');
  return competitions.filter((c) =>
    norm(c).split(' ').some((word) => word.length >= 5 && !GENERIC.has(word) && name.includes(word)),
  );
}

function loadAll(): Record<string, SetChoice> {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? '{}') as unknown;
    if (raw && typeof raw === 'object') return raw as Record<string, SetChoice>;
  } catch { /* storage unavailable or garbage */ }
  return {};
}

function readStyle(): TickerStyle {
  try {
    const raw = JSON.parse(localStorage.getItem(STYLE_KEY) ?? 'null') as Partial<TickerStyle> | null;
    if (raw && typeof raw === 'object') return { ...DEFAULT_STYLE, ...raw };
    return { ...DEFAULT_STYLE, logos: localStorage.getItem(LOGOS_KEY) === '1' };
  } catch {
    return { ...DEFAULT_STYLE };
  }
}

class Ticker {
  fixtures = $state<Fixture[]>([]);
  /** fixture key -> when its score last changed, for the highlight. */
  changedAt = $state<Record<string, number>>({});
  now = $state(Date.now());

  #setKey: string | null = null;
  #setName = '';
  #choice = $state<SetChoice | null>(null);
  #timer: number | null = null;
  #healed = false;

  /** Competitions with anything to show right now. */
  readonly competitions = $derived([...new Set(this.fixtures.map((f) => f.competition))].sort());

  readonly on = $derived(this.#choice?.on ?? false);
  readonly goals = $derived(this.#choice?.goals ?? false);
  readonly cards = $derived(this.#choice?.cards ?? false);
  readonly yellow = $derived(this.#choice?.yellow ?? false);
  /** The goal being announced, then the ones waiting behind it. */
  banners = $state<GoalBanner[]>([]);
  notes = $state<CardNote[]>([]);
  /** fixture key -> incident ids already known, so each is announced once. */
  #seenIncidents = new Map<string, Set<string>>();
  /** Goals seen in the score, waiting for their scorer before the banner. */
  #pending: Array<{ key: string; side: 'home' | 'away' | null; since: number }> = [];
  #pendingTimer: number | null = null;
  #bannerId = 0;
  #bannerTimer: number | null = null;
  readonly selected = $derived(this.#choice?.competitions ?? []);

  readonly visible = $derived(
    this.on ? this.fixtures.filter((f) => this.selected.includes(f.competition)) : [],
  );

  /** Called when a set starts; `id` null for an ad-hoc selection. */
  open(id: number | null, name: string): void {
    this.#setKey = id === null ? 'adhoc' : String(id);
    this.#setName = name;
    this.#choice = loadAll()[this.#setKey] ?? null;
    this.#healed = false;
    this.changedAt = {};
    this.#seenIncidents.clear();
    this.#pending = [];
    if (this.#timer === null) {
      void this.#poll();
      this.#timer = window.setInterval(() => void this.#poll(), POLL_MS);
    }
  }

  close(): void {
    if (this.#timer !== null) clearInterval(this.#timer);
    this.#timer = null;
    this.#setKey = null;
    this.#pending = [];
    this.#clearBanners();
  }

  #current(): SetChoice {
    return { on: this.on, competitions: this.selected, goals: this.goals, cards: this.cards, yellow: this.yellow };
  }

  setOn(on: boolean): void {
    this.#save({ ...this.#current(), on, competitions: on ? this.#withDefault() : this.selected });
  }

  /** Goal banners share the ticker's competitions, but not its on/off. */
  setGoals(goals: boolean): void {
    this.#save({ ...this.#current(), goals, competitions: goals ? this.#withDefault() : this.selected });
    if (!goals) this.#clearBanners();
  }

  setCards(cards: boolean): void {
    this.#save({ ...this.#current(), cards, competitions: cards ? this.#withDefault() : this.selected });
    if (!cards) this.#clearBanners();
  }

  setYellow(yellow: boolean): void {
    this.#save({ ...this.#current(), yellow, competitions: yellow ? this.#withDefault() : this.selected });
    if (!yellow) this.notes = [];
  }

  toggleCompetition(name: string): void {
    const has = this.selected.includes(name);
    const competitions = has ? this.selected.filter((c) => c !== name) : [...this.selected, name];
    const any = competitions.length > 0;
    this.#save({
      on: this.on && any, competitions, goals: this.goals && any, cards: this.cards && any, yellow: this.yellow && any,
    });
  }

  style = $state(readStyle());

  setStyle(change: Partial<TickerStyle>): void {
    this.style = { ...this.style, ...change };
    try {
      localStorage.setItem(STYLE_KEY, JSON.stringify(this.style));
    } catch { /* storage unavailable */ }
  }

  /** Whether any of the on-screen announcements wants the match list. */
  readonly announcing = $derived(this.goals || this.cards || this.yellow);

  /** First time on: what the set's name suggests, else everything. */
  #withDefault(): string[] {
    if (this.selected.length > 0) return this.selected;
    const guess = guessCompetitions(this.#setName, this.competitions);
    return guess.length > 0 ? guess : [...this.competitions];
  }

  /**
   * Cards and penalties new since the last poll. A match seen for the first
   * time only sets its baseline, so joining late replays nothing.
   */
  #checkIncidents(f: Fixture, known: boolean): void {
    const list = f.extra?.incidents;
    // A poll without the list (a detail read failed) says nothing; treating it
    // as "no cards" would announce every earlier one again on the next poll.
    if (!list) return;
    const key = keyOf(f);
    const seen = this.#seenIncidents.get(key);
    // Remembered, not replaced: an event that briefly drops out is not new when it returns.
    this.#seenIncidents.set(key, new Set([...(seen ?? []), ...list.map((i) => i.id)]));
    if (!known || !seen || !this.selected.includes(f.competition)) return;
    for (const incident of list) {
      if (seen.has(incident.id)) continue;
      if (incident.kind === 'yellow') {
        if (this.yellow) this.#note(f, incident);
      } else if (this.cards) {
        this.#announce(f, incident.side, incident);
      }
    }
  }

  #note(fixture: Fixture, incident: Incident): void {
    const id = ++this.#bannerId;
    // At most three at once; a flurry of bookings should not fill the corner.
    this.notes = [...this.notes, { id, fixture, incident }].slice(-3);
    window.setTimeout(() => { this.notes = this.notes.filter((n) => n.id !== id); }, NOTE_MS);
  }

  #announce(f: Fixture, side: 'home' | 'away' | null, incident?: Incident): void {
    this.banners = [...this.banners, { id: ++this.#bannerId, fixture: f, side, incident }];
    if (this.#bannerTimer === null) this.#scheduleNext();
    // One extra look halfway through, for a scorer named just after the score.
    window.setTimeout(() => { if (this.#timer !== null) void this.#poll(); }, BANNER_MS / 2);
  }

  /** Ends the banner showing now; the next one, if any, follows at once. */
  dismissBanner(): void {
    if (this.#bannerTimer !== null) clearTimeout(this.#bannerTimer);
    this.#bannerTimer = null;
    this.banners = this.banners.slice(1);
    if (this.banners.length > 0) this.#scheduleNext();
  }

  #scheduleNext(): void {
    this.#bannerTimer = window.setTimeout(() => {
      this.banners = this.banners.slice(1);
      this.#bannerTimer = null;
      if (this.banners.length > 0) this.#scheduleNext();
    }, BANNER_MS);
  }

  /**
   * The scorer often arrives a poll or two after the score. A banner showing
   * or waiting takes the newer details of its match, as long as the score is
   * still the one it announces.
   */
  #refreshBanners(next: Fixture[]): void {
    if (this.banners.length === 0) return;
    const latest = new Map(next.map((f) => [keyOf(f), f]));
    let changed = false;
    const banners = this.banners.map((b) => {
      const f = latest.get(keyOf(b.fixture));
      if (!f || f === b.fixture || f.homeScore !== b.fixture.homeScore || f.awayScore !== b.fixture.awayScore) return b;
      changed = true;
      return { ...b, fixture: f };
    });
    if (changed) this.banners = banners;
  }

  #clearBanners(): void {
    if (this.#bannerTimer !== null) clearTimeout(this.#bannerTimer);
    this.#bannerTimer = null;
    this.banners = [];
    this.notes = [];
  }

  isHighlighted(f: Fixture): boolean {
    const at = this.changedAt[keyOf(f)];
    return at !== undefined && this.now - at < HIGHLIGHT_MS;
  }

  #save(choice: SetChoice): void {
    this.#choice = choice;
    if (!this.#setKey) return;
    try {
      const all = loadAll();
      all[this.#setKey] = choice;
      localStorage.setItem(KEY, JSON.stringify(all));
    } catch { /* only a convenience */ }
  }

  async #poll(): Promise<void> {
    this.now = Date.now();
    let next: Fixture[];
    try {
      next = (await api.ticker()).fixtures;
    } catch {
      return;
    }
    // The first load sets the baseline: only changes seen while watching count.
    if (this.fixtures.length > 0) {
      const before = new Map(this.fixtures.map((f) => [keyOf(f), f]));
      const changed = { ...this.changedAt };
      for (const f of next) {
        const old = before.get(keyOf(f));
        if (old && (old.homeScore !== f.homeScore || old.awayScore !== f.awayScore)) {
          changed[keyOf(f)] = this.now;
          // Only a goal, not a correction: a VAR-annulled goal lowers the score.
          const home = (f.homeScore ?? 0) - (old.homeScore ?? 0);
          const away = (f.awayScore ?? 0) - (old.awayScore ?? 0);
          const key = keyOf(f);
          // Whatever was waiting for this match is overtaken: a disallowed
          // goal is no goal, and before a second one the first goes out as is.
          const waiting = this.#pending.filter((p) => p.key === key);
          this.#pending = this.#pending.filter((p) => p.key !== key);
          if (home + away > 0) for (const p of waiting) this.#announce(old, p.side);
          if (this.goals && this.selected.includes(f.competition) && home + away > 0) {
            this.#pending.push({ key, side: home > 0 && away === 0 ? 'home' : away > 0 && home === 0 ? 'away' : null, since: this.now });
          }
        }
        this.#checkIncidents(f, !!old);
      }
      this.changedAt = changed;
      this.#refreshBanners(next);
    } else {
      for (const f of next) this.#checkIncidents(f, false);
    }
    this.fixtures = next;
    this.#releasePending(next);
    this.#healSelection();
  }

  /** Banners for waiting goals whose scorer is now known, or that waited long enough. */
  #releasePending(next: Fixture[]): void {
    if (this.#pending.length === 0) return;
    const latest = new Map(next.map((f) => [keyOf(f), f]));
    this.#pending = this.#pending.filter((p) => {
      const f = latest.get(p.key);
      if (!f) return false;
      if (!scorerKnown(f, p.side) && this.now - p.since < SCORER_WAIT_MS) return true;
      this.#announce(f, p.side);
      return false;
    });
    if (this.#pending.length > 0 && this.#pendingTimer === null && this.#timer !== null) {
      this.#pendingTimer = window.setTimeout(() => {
        this.#pendingTimer = null;
        if (this.#timer !== null) void this.#poll();
      }, PENDING_POLL_MS);
    }
  }

  /**
   * A saved choice whose competitions all disappeared was most likely saved
   * under an old name (the proxy translated "UEFA Champions League" to
   * "Champions League"). Rather than a ticker that silently shows nothing,
   * the set's name gets another say -- once per match, and only if it finds
   * something among what is actually on.
   */
  #healSelection(): void {
    const choice = this.#choice;
    if (this.#healed || !choice || this.competitions.length === 0) return;
    this.#healed = true;
    if (choice.competitions.length === 0 || choice.competitions.some((c) => this.competitions.includes(c))) return;
    const guess = guessCompetitions(this.#setName, this.competitions);
    if (guess.length > 0) this.#save({ ...choice, competitions: guess });
  }
}

const keyOf = (f: Fixture) => `${f.provider}:${f.externalId}`;

/**
 * Whether the fixture names the scorer of the goal that made its current
 * score. A provider without goal details at all (football-data) has nothing
 * to wait for.
 */
function scorerKnown(f: Fixture, side: 'home' | 'away' | null): boolean {
  if (!f.extra) return true;
  const g = f.extra.lastGoal;
  if (!g?.scorer) return false;
  if (g.score && (g.score[0] !== f.homeScore || g.score[1] !== f.awayScore)) return false;
  return side === null || g.side === null || g.side === side;
}

export const ticker = new Ticker();
